import React, { useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import { ArrowSquareOut, Desktop, DeviceMobile } from "@phosphor-icons/react";
import { AdminModal } from "@/components/pageSettings/AdminModal";
import { funnelTitle, picksOrAll, VERTICAL_FUNNELS } from "@/lib/funnelOptions";
import { ShareReviewLink } from "@/components/thankyou/ShareReviewLink";
import { LayoutPicker, useKeepLayout } from "@/components/thankyou/LayoutPicker";
import { LayoutResults } from "@/components/thankyou/LayoutResults";

const PILL = "border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100";
const pill = (on) => `${PILL} ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;

function experienceFor(funnel) {
  if (funnel === "focused") return "focused_funnel";
  if (funnel === "injury" || VERTICAL_FUNNELS.includes(funnel)) return "case_quiz";
  return null;
}

// Same thank-you URL a visitor lands on after a lead from `funnel` goes to this buyer (preview=1: no pixels or stats).
// `experience` overrides the funnel's default look (a buyer's own page uses its own form style).
export function previewUrl(code, funnel, demo, experience) {
  const q = new URLSearchParams({ preview: "1", slug: code, b: code });
  if (funnel) q.set("funnel", funnel);
  const exp = experience === undefined ? experienceFor(funnel) : experience;
  if (exp) q.set("experience", exp);
  if (demo) q.set("demo", demo);
  return `/lp/thank-you?${q}`;
}

export function ThankYouPreviewDialog({ b, onClose, onSaved }) {
  const funnels = picksOrAll(b.picks);
  const [funnel, setFunnel] = useState(funnels[0]);
  const [phone, setPhone] = useState(false);
  const [tryKey, setTryKey] = useState(null);
  const layout = useKeepLayout(b, onSaved, () => setTryKey(null));
  const p = `ty-preview-${b.code}`;
  const base = previewUrl(b.code, funnel);
  const url = tryKey ? `${base}&ty_design=${encodeURIComponent(tryKey)}` : base;
  return (
    <AdminModal eyebrow="Thank-you page preview" title={b.firm_name} onClose={onClose} testId={p} wide>
      <p className="mb-2 text-[11px] text-zinc-600">
        Exactly what a visitor sees after their lead goes to {b.firm_name}. It looks a little different on each funnel, so pick one.
        Nothing is tracked or counted here.
      </p>
      {b.is_draft && (
        <p className="mb-2 border border-amber-700 bg-amber-100 px-2 py-1 text-[11px]" data-testid={`${p}-draft-note`}>
          This buyer is still a draft, so real visitors won't see this page until setup is finished.
        </p>
      )}
      {onSaved && <LayoutPicker b={b} tryKey={tryKey} onTry={setTryKey} {...layout} />}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {funnels.map((f) => (
          <button key={f} type="button" onClick={() => setFunnel(f)} className={pill(f === funnel)} data-testid={`${p}-funnel-${f}`}>
            {funnelTitle(f)}
          </button>
        ))}
        <span className="ml-auto inline-flex gap-1.5">
          <button type="button" onClick={() => setPhone(false)} className={pill(!phone)} data-testid={`${p}-desktop`}>
            <Desktop size={11} weight="bold" /> Desktop
          </button>
          <button type="button" onClick={() => setPhone(true)} className={pill(phone)} data-testid={`${p}-phone`}>
            <DeviceMobile size={11} weight="bold" /> Phone
          </button>
          <a href={publicLink(url)} target="_blank" rel="noopener noreferrer" className={pill(false)} data-testid={`${p}-open-tab`}>
            <ArrowSquareOut size={11} weight="bold" /> New tab
          </a>
        </span>
      </div>
      <div className="flex justify-center bg-zinc-100 p-2">
        <iframe key={url} title={`${b.firm_name} thank-you page`} src={publicLink(url)} data-testid={`${p}-frame`}
                className={`h-[70vh] border-2 border-black bg-white ${phone ? "w-[390px]" : "w-full"}`} />
      </div>
      {onSaved && <LayoutResults b={b} {...layout} />}
      <ShareReviewLink b={b} />
    </AdminModal>
  );
}
